/**
 * 辩题 / 资料库面板（布局稿 wf-07 的 Material Tab）。
 *
 * 当前只内置一套 MotionPackage；本面板把「赛制/辩题/资料库/DAG/知识卡」
 * 的关系显式呈现出来，并为 Profile 系统预留选择器与导入入口。
 */
import { useMemo, useState } from 'react';
import { getStage, type MotionPackage } from '@zongheng/core-schema';
import { Badge, Button } from '../ui/primitives.tsx';

type MaterialTab = 'library' | 'cards' | 'dag';

export function MotionLibraryPanel({
  motion,
  onOpenDag,
}: {
  motion: MotionPackage;
  onOpenDag?: () => void;
}) {
  const [tab, setTab] = useState<MaterialTab>('library');
  const [motionId, setMotionId] = useState(motion.motionId);
  const [reloading, setReloading] = useState(false);
  const [loadNote, setLoadNote] = useState(`已加载 ${motion.evidenceLibrary.length} 条论据`);

  const counts = useMemo(
    () => ({
      evidence: motion.evidenceLibrary.length,
      constructive: motion.constructiveCards.length,
      crossEx: motion.crossExCards.length,
      sandbox: motion.sandboxCards.length,
      dagNodes: motion.dag.nodes.length,
      dagEdges: motion.dag.edges.length,
    }),
    [motion],
  );

  const load = () => {
    setReloading(true);
    setLoadNote('正在加载资料库 / DAG / 知识卡…');
    window.setTimeout(() => {
      setReloading(false);
      setLoadNote(
        `已重新加载：${counts.evidence} 条论据 · ${counts.constructive} 张申论卡 · ${counts.dagNodes} 个 DAG 节点`,
      );
    }, 650);
  };

  return (
    <div className="space-y-3" data-testid="motion-library">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="micro-label">MOTION / MATERIAL</div>
          <h2 className="mt-1 text-[16px] font-semibold text-metal">辩题与资料库</h2>
          <p className="mt-1 text-[11px] text-slate-500">
            Format × Motion × Rules 共同组成比赛蓝图；切换后应加载对应资料库/DAG/知识卡。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone="pass">已锁定</Badge>
          <Button
            size="sm"
            variant="secondary"
            onClick={load}
            disabled={reloading}
            testId="motion-reload"
          >
            {reloading ? '加载中…' : '重新加载资料库'}
          </Button>
        </div>
      </header>

      <section className="lens-inset p-3">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <label className="block">
            <span className="micro-label">MOTION PACKAGE</span>
            <select
              data-testid="motion-selector"
              className="mt-1.5 w-full rounded-md border border-white/10 bg-black/30 px-2.5 py-2 text-[12px] text-slate-200 outline-none focus:border-white/25"
              value={motionId}
              onChange={(event) => {
                setMotionId(event.target.value);
                setLoadNote(`已选择：${motion.title}`);
              }}
            >
              <option value={motion.motionId}>
                {motion.title} · {motion.motion}
              </option>
            </select>
          </label>
          <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
            <span className="pill">v{motion.version}</span>
            <span className="pill indigo">hash {motion.motionId.slice(-8)}</span>
            <span className="pill">仅内置 1 套 · 多题库 Phase 2</span>
          </div>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
          {motion.motion}
        </p>
        <p className="mt-1 text-[10px] text-slate-500" data-testid="motion-load-note">
          {loadNote}
        </p>
      </section>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          ['论据', counts.evidence],
          ['申论卡', counts.constructive],
          ['质询卡', counts.crossEx],
          ['沙盘', counts.sandbox],
        ].map(([label, value]) => (
          <div key={String(label)} className="material-tile">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mono mt-1 text-[18px] font-semibold text-slate-100">{value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-4 border-b border-white/[0.07] pb-1">
        {(
          [
            ['library', '资料库'],
            ['cards', '知识卡'],
            ['dag', 'DAG'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`material-tab-${key}`}
            data-active={tab === key}
            onClick={() => setTab(key)}
            className="coach-tab px-0 pb-1.5"
          >
            {label}
          </button>
        ))}
        <span className="ml-auto text-[10px] text-slate-500">
          {counts.evidence} 条论据 · {counts.dagNodes} 节点 / {counts.dagEdges} 边
        </span>
      </div>

      {tab === 'library' && (
        <div className="lens-inset max-h-[300px] overflow-auto p-1" data-testid="motion-evidence-list">
          {motion.evidenceLibrary.slice(0, 12).map((evidence) => (
            <div
              key={evidence.id}
              className="flex items-start justify-between gap-3 border-b border-white/[0.05] px-2 py-2 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="truncate text-[11px] font-medium text-slate-200">
                  {evidence.shortTitle}
                </div>
                <div className="mt-0.5 line-clamp-1 text-[10px] text-slate-500">
                  {evidence.exactQuoteOrStats}
                </div>
              </div>
              <span className="mono shrink-0 text-[9px] text-slate-600">{evidence.id}</span>
            </div>
          ))}
          <div className="px-2 py-2 text-[10px] text-slate-500">
            仅展示前 12 条；完整论据库共 {counts.evidence} 条。
          </div>
        </div>
      )}

      {tab === 'cards' && (
        <div className="grid gap-2 md:grid-cols-2" data-testid="motion-card-list">
          {motion.constructiveCards.map((card) => (
            <div key={card.cardId} className="material-tile">
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-[9px] text-slate-500">
                  {getStage(card.stageIndex)?.name ?? `环节 ${card.stageIndex}`}
                </span>
                <span className="pill indigo">申论</span>
              </div>
              <div className="mt-1 text-[12px] font-medium text-slate-200">{card.title}</div>
              <div className="mt-1 line-clamp-2 text-[10px] leading-relaxed text-slate-500">
                {card.coreMission}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="pill">{card.archetype}</span>
                {card.pillTags?.slice(0, 2).map((tag) => (
                  <span key={tag} className="pill">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {motion.crossExCards.slice(0, 4).map((card) => (
            <div key={card.cardId} className="material-tile">
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-[9px] text-slate-500">
                  {getStage(card.stageIndex)?.name ?? `环节 ${card.stageIndex}`}
                </span>
                <span className="pill amber">质询</span>
              </div>
              <div className="mt-1 text-[12px] font-medium text-slate-200">
                {card.targetRole} · {card.mode === 'TARGETED_COUNTER' ? '定向克制' : '全盲侦察'}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'dag' && (
        <div className="lens-inset p-4" data-testid="motion-dag-summary">
          <div className="flex items-center justify-between">
            <div>
              <div className="micro-label">DEBATE DAG</div>
              <div className="mt-1 text-[13px] font-medium text-slate-200">
                {counts.dagNodes} 个节点 · {counts.dagEdges} 条边
              </div>
              <p className="mt-1 text-[10px] text-slate-500">
                按环节范围与正反方路径组织；点击节点可查看论据与反制。
              </p>
            </div>
            {onOpenDag && (
              <Button size="sm" variant="secondary" onClick={onOpenDag} testId="motion-open-dag">
                在 DAG 视图打开
              </Button>
            )}
          </div>
          <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {motion.dag.nodes.slice(0, 8).map((node) => (
              <div key={node.id} className="flex items-center justify-between gap-2 rounded bg-white/[0.02] px-2 py-1.5">
                <span className="truncate text-[10.5px] text-slate-300">{node.label}</span>
                <span className={`pill ${node.side === 'AFF' ? 'ice' : 'rose'}`}>
                  {node.side}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="lens-inset flex flex-wrap items-center justify-between gap-2 p-3">
        <div>
          <div className="text-[11px] text-slate-300">导入新辩题 / 赛制包</div>
          <div className="text-[10px] text-slate-500">
            Profile 系统落地后支持 JSON/URL 导入、hash 校验与离线缓存。
          </div>
        </div>
        <Button size="sm" variant="ghost" disabled testId="motion-import">
          Phase 2
        </Button>
      </section>
    </div>
  );
}
